import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getCachedUser } from '../lib/api/auth';
import { X, Lock, Rocket } from 'lucide-react';
import marsGlobeLogo from '../assets/mars-globe.png';
import { useMarsVersion } from '../lib/marsVersion';
import '../styles/explore-mars.css';

// Direct to the real, Turnstile-protected domain — www.teachgenie.ai/mars/
// now immediately redirects here anyway (see frontend-mars/src/main.jsx),
// this just skips that extra hop for the site's own "Explore MARS" CTA.
const MARS_URL = 'https://mars.teachgenie.ai/';
const EASE = [0.16, 1, 0.3, 1];

/* ── Orb: the real MARS brand mark. No separate orbit ring/moon here
   anymore — the logo artwork already has its own ring baked in, and a
   second animated ring around it just doubled up. ──────────────────────── */
function MarsOrb({ size = 'small' }) {
  return (
    <div className={`xm-orb xm-orb--${size}`}>
      <div className="xm-orb-glow" aria-hidden="true" />
      <div className="xm-planet-spin">
        <img src={marsGlobeLogo} alt="MARS" className="xm-planet-svg" />
      </div>
    </div>
  );
}

/* ── Star field for the modal backdrop ──────────────────────────────────── */
function StarField() {
  return (
    <div className="xm-stars" aria-hidden="true">
      {Array.from({ length: 26 }).map((_, i) => (
        <span key={i} className={`xm-star xm-star-${i % 5}`} style={{
          left: `${(i * 37) % 100}%`,
          top: `${(i * 53) % 100}%`,
          animationDelay: `${(i % 7) * 0.6}s`,
        }} />
      ))}
      <span className="xm-shooting-star" />
    </div>
  );
}

export default function ExploreMars() {
  const version = useMarsVersion();
  const { user, loading } = useAuth();
  const [gateOpen, setGateOpen] = useState(false);

  // Issue #196: the real auth token is an HttpOnly cookie this JS can't
  // read — the cached (non-sensitive) user object is the same "was
  // logged in" hint the token used to be, for the brief window before
  // AuthContext's own /me call resolves.
  const isMember = !!user || (loading && !!getCachedUser());

  const handleLaunch = () => {
    if (isMember) {
      window.location.assign(MARS_URL);
    } else {
      setGateOpen(true);
    }
  };

  const close = useCallback(() => setGateOpen(false), []);

  // Escape key + scroll lock while the gate modal is open
  useEffect(() => {
    if (!gateOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [gateOpen, close]);

  return (
    <>
      {/* ── Launcher — now an inline hero CTA, reveal timing owned by the
          hero-buttons row's shared .reveal-up wrapper, not its own delay ── */}
      <motion.button
        type="button"
        className="xm-launcher"
        onClick={handleLaunch}
        aria-label="Explore MARS"
        whileHover={{ y: -4 }}
        whileTap={{ scale: 0.96 }}
      >
        <MarsOrb size="small" />
        <span className="xm-label">
          <span className="xm-label-top">
            <span className="xm-label-new">NEW</span> MISSION
          </span>
          <span className="xm-label-main">
            Explore MARS
            <span className="xm-label-version">{version}</span>
          </span>
        </span>
        <span className="xm-label-arrow" aria-hidden="true">→</span>
      </motion.button>

      {/* ── Members-only gate ── */}
      {createPortal(
        <AnimatePresence>
          {gateOpen && (
            <motion.div
              className="xm-gate-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.28 }}
              onClick={close}
            >
              <motion.div
                className="xm-gate-card"
                role="dialog"
                aria-modal="true"
                aria-label="Explore MARS is for TeachGenie members"
                initial={{ opacity: 0, y: 42, scale: 0.92, filter: 'blur(10px)' }}
                animate={{ opacity: 1, y: 0, scale: 1, filter: 'blur(0px)' }}
                exit={{ opacity: 0, y: 26, scale: 0.94, filter: 'blur(8px)' }}
                transition={{ duration: 0.5, ease: EASE }}
                onClick={(e) => e.stopPropagation()}
              >
                <StarField />
                <button className="xm-gate-close" onClick={close} aria-label="Close">
                  <X size={18} />
                </button>

                <div className="xm-gate-planet">
                  <MarsOrb size="large" />
                </div>

                <div className="xm-gate-eyebrow">
                  <Lock size={12} />
                  <span>MEMBERS-ONLY TERRITORY</span>
                </div>

                <h3 className="xm-gate-title">MARS awaits, explorer</h3>

                <p className="xm-gate-text">
                  Explore MARS is exclusive to TeachGenie members. Please sign up
                  on TeachGenie to unlock it — then launch straight into the MARS
                  experience.
                </p>

                <div className="xm-gate-actions">
                  <Link to="/signup" className="xm-gate-cta" onClick={close}>
                    <Rocket size={16} />
                    Sign up on TeachGenie
                  </Link>
                  <Link to="/login" className="xm-gate-login" onClick={close}>
                    Already a member? <b>Log in</b>
                  </Link>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </>
  );
}
